import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, BarChart3 } from "lucide-react";
import { formatMoney } from "@/lib/money";
import { toast } from "sonner";

interface DayRow {
  day: string;
  tx_count: number;
  volume_cents: number;
}

const todayISO = () => new Date().toISOString().slice(0, 10);
const isoDaysAgo = (n: number) =>
  new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);

const AdminReports = () => {
  const [from, setFrom] = useState(isoDaysAgo(29));
  const [to, setTo] = useState(todayISO());
  const [rows, setRows] = useState<DayRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const days = Math.max(
      1,
      Math.ceil((new Date(to).getTime() - new Date(from).getTime()) / 86_400_000) + 1,
    );
    const { data, error } = await supabase.rpc("admin_tx_series", { _days: days });
    if (error) toast.error(error.message);
    const filtered = (data ?? []).filter((r: DayRow) => r.day >= from && r.day <= to);
    setRows(filtered as DayRow[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalTx = rows.reduce((s, r) => s + Number(r.tx_count), 0);
  const totalVol = rows.reduce((s, r) => s + Number(r.volume_cents), 0);

  const downloadCsv = () => {
    const lines = ["day,tx_count,volume_cents,volume_usd"];
    rows.forEach((r) => {
      lines.push(
        `${r.day},${r.tx_count},${r.volume_cents},${(Number(r.volume_cents) / 100).toFixed(2)}`,
      );
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cashisle-volume-${from}-to-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          Volume reports
        </h1>
        <p className="text-sm text-muted-foreground">
          Daily transaction count and volume. Export to CSV for accounting.
        </p>
      </div>

      <Card className="p-4 flex items-end gap-2 flex-wrap">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <Button onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </Button>
        <Button variant="outline" onClick={downloadCsv} disabled={rows.length === 0}>
          <Download className="h-4 w-4" /> CSV
        </Button>
      </Card>

      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4">
          <div className="text-xs text-muted-foreground">Transactions</div>
          <div className="text-2xl font-semibold">{totalTx.toLocaleString()}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-muted-foreground">Volume</div>
          <div className="text-2xl font-semibold">{formatMoney(totalVol)}</div>
        </Card>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-3 py-2">Day</th>
              <th className="text-right px-3 py-2">Tx count</th>
              <th className="text-right px-3 py-2">Volume</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.day} className="border-t border-border">
                <td className="px-3 py-2">{r.day}</td>
                <td className="px-3 py-2 text-right">{Number(r.tx_count).toLocaleString()}</td>
                <td className="px-3 py-2 text-right">{formatMoney(Number(r.volume_cents))}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={3} className="px-3 py-8 text-center text-muted-foreground">
                  No data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default AdminReports;
